import React, { useState } from "react";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { Camera, Star } from "lucide-react";
import { Rating } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";

import { Input, Label, Button } from "components/ui";
import { InputTextarea, SubmitLoaderButton } from "components/atoms";
import { AddReviewsAction } from "store/middlewares/reviews";
import { openToast } from "store/slices/common/toast.slice";
import { closeAdminModal } from "store/slices/common/adminModal.slice";
import BlankImg from "assets/images/PNG/blank.png";

function ModalEditReviews({ row, filter }) {
  const dispatch = useDispatch();
  const [formError, setFormError] = useState(false);
  const [previewImage, setPreviewImage] = useState();
  const { settings } = useSelector((state) => state.settings);

  const handleSubmit = (values) => {
    if (!row) {
      dispatch(
        AddReviewsAction({
          ...values,
          firstName: values.first_name.trim(),
          lastName: values.last_name.trim(),
          description: values.description.trim(),
          filter,
          onSuccess: () => formik.setSubmitting(false),
          onError: () => setFormError(true),
        }),
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      first_name: row?.first_name ?? "",
      last_name: row?.last_name ?? "",
      description: row?.description ?? "",
      star: row?.star ?? 0,
      file: null,
      image: null,
    },
    validationSchema: Yup.object({
      first_name: Yup.string().required("First name is required"),
      last_name: Yup.string().required("Last name is required"),
      description: Yup.string().required("Description is required"),
      star: Yup.number()
        .typeError("Rating is required")
        .min(0.5, "Please select a rating")
        .required("Rating is required"),
      image: Yup.string().required("Image is required"),
    }),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  // --------------------- image change ------------------------------
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileSizeLimit = settings?.max_file_size * 1024 * 1024;

    // Check image type
    if (!file.type.match(/^image\/(png|jpeg|jpg?)$/)) {
      dispatch(
        openToast({
          type: "error",
          message: "Only images (PNG, JPG, JPEG) are allowed.",
        }),
      );
      return;
    }

    // Check image size
    if (file.size > fileSizeLimit) {
      dispatch(
        openToast({
          type: "error",
          message: `Please upload images less than ${settings?.max_file_size}MB.`,
        }),
      );
      return;
    }

    // Check corrupted image
    const isValidImage = await new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(true);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(false);
      };
      img.src = objectUrl;
    });

    if (!isValidImage) {
      dispatch(
        openToast({
          type: "error",
          message: "This is corrupted image please upload valid image.",
        }),
      );
      event.target.value = "";
      return;
    }

    // Valid image
    const reader = new FileReader();
    reader.onload = () => {
      formik.setFieldValue("image", reader.result);
      formik.setFieldValue("file", file);
      formik.setFieldError("image", "");
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <form onSubmit={formik.handleSubmit} className="grid gap-5">
      {/* Title with icon */}
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <Star className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">Add review</h2>
      </div>

      {/* Photo */}
      <div className="grid justify-items-center gap-1">
        <div className="relative">
          <div className="size-24 overflow-hidden rounded-full">
            <img
              src={previewImage ?? BlankImg}
              alt="Reviewer"
              className="h-full w-full object-cover"
            />
          </div>
          <input
            type="file"
            name="file"
            accept="image/png, image/jpeg, image/jpg"
            id="editimg"
            onChange={handleFileChange}
            hidden
          />
          <label
            htmlFor="editimg"
            title="Upload photo"
            className="absolute bottom-0 right-0 flex size-8 cursor-pointer items-center justify-center rounded-full border border-border bg-white text-foreground shadow-sm"
          >
            <Camera className="size-4" aria-hidden="true" />
            <span className="sr-only">Upload photo</span>
          </label>
        </div>
        {formik.errors.image && formik.touched.image && (
          <div className="text-[0.8125rem] text-red-500">
            {formik.errors.image}
          </div>
        )}
      </div>

      {/* Names side by side */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="first_name">First name</Label>
          <Input
            id="first_name"
            className="!py-0 w-full mt-2"
            variant="lg"
            error={formik.errors.first_name && formik.touched.first_name}
            errorMsg={formik.errors.first_name}
            value={formik.values.first_name}
            isTextOnly={true}
            isSpace={true}
            maxLength={50}
            type="text"
            name="first_name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter first name"
          />
        </div>
        <div>
          <Label htmlFor="last_name">Last name</Label>
          <Input
            id="last_name"
            className="!py-0 w-full mt-2"
            variant="lg"
            error={formik.errors.last_name && formik.touched.last_name}
            errorMsg={formik.errors.last_name}
            value={formik.values.last_name}
            isTextOnly={true}
            isSpace={true}
            maxLength={50}
            type="text"
            name="last_name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter last name"
          />
        </div>
      </div>

      {/* Rating */}
      <div>
        <Label>Rating</Label>
        <div className="mt-2">
          <Rating
            name="star"
            size="large"
            value={formik.values.star}
            precision={0.5}
            onChange={(event, newValue) => {
              // clicking the selected star again gives null, so keep it at 0 (no rating)
              formik.setFieldValue("star", newValue ?? 0);
            }}
            emptyIcon={<StarIcon fontSize="inherit" />}
            sx={{
              "& .MuiRating-icon": { mx: "1px" },
              "& .MuiRating-iconEmpty": { mx: "1px" },
            }}
          />
        </div>
        {formik.errors.star && formik.touched.star && (
          <div className="mt-1 text-[0.8125rem] text-red-500">
            {formik.errors.star}
          </div>
        )}
      </div>

      {/* Description */}
      <div>
        <Label htmlFor="description">Description</Label>
        <InputTextarea
          id="description"
          error={formik.errors.description && formik.touched.description}
          errorMsg={formik.errors.description}
          value={formik.values.description}
          isTextOnly={true}
          maxLength={200}
          type="text"
          name="description"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className={"flex flex-col !py-0 w-full"}
          placeholder="Enter description"
          textareaClassName={"rounded-lg w-full px-3 py-2 mt-2 no-scrollbar"}
          isRequired={false}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => dispatch(closeAdminModal())}
          sx={{height:"70px"}}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          btnText="Add review"
          isDisable={formik.isSubmitting && !formError}
        />
      </div>
    </form>
  );
}

export default ModalEditReviews;